/**
 * Shared Lens API service module.
 * Single source for all Lens OCR operations — used by LuminaLens, LensDemoDialog, LuminaDoctorMode.
 *
 * Endpoints:
 *   POST   /api/lens/extract          — upload + extraction
 *   GET    /api/lens/jobs/{jobId}      — poll status
 *   GET    /api/lens/jobs              — list history
 *   DELETE /api/lens/jobs/{jobId}      — remove job
 *   POST   /api/lens/jobs/{jobId}/retry — retry failed
 */

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Upload a file and start extraction.
 * @param {File} file - The file to upload
 * @param {object} [opts] - Optional params
 * @param {string} [opts.patientId] - Associate with patient
 * @param {string} [opts.documentTypeHint] - Hint for document type
 * @returns {Promise<object>} Job object {job_id, status, progress, extracted_fields, ...}
 */
export async function lensExtract(file, opts = {}) {
  const form = new FormData();
  form.append('file', file);
  if (opts.patientId) form.append('patient_id', opts.patientId);
  if (opts.documentTypeHint) form.append('document_type_hint', opts.documentTypeHint);

  const res = await fetch(`${API}/api/lens/extract`, { method: 'POST', body: form });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Upload failed (${res.status})`);
  }
  return res.json();
}

/**
 * Poll a job's status.
 * @param {string} jobId
 * @returns {Promise<object>} Job object
 */
export async function lensPollJob(jobId) {
  const res = await fetch(`${API}/api/lens/jobs/${jobId}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Poll failed (${res.status})`);
  }
  return res.json();
}

/**
 * List job history.
 * @param {object} [opts]
 * @param {string} [opts.patientId] - Filter by patient
 * @param {number} [opts.page]
 * @param {number} [opts.limit]
 * @returns {Promise<{jobs: object[], total: number}>}
 */
export async function lensListJobs(opts = {}) {
  const params = new URLSearchParams();
  if (opts.patientId) params.set('patient_id', opts.patientId);
  if (opts.page) params.set('page', String(opts.page));
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();

  const res = await fetch(`${API}/api/lens/jobs${qs ? `?${qs}` : ''}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `List failed (${res.status})`);
  }
  return res.json();
}

/**
 * Delete a job.
 * @param {string} jobId
 * @returns {Promise<object>}
 */
export async function lensDeleteJob(jobId) {
  const res = await fetch(`${API}/api/lens/jobs/${jobId}`, { method: 'DELETE' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Delete failed (${res.status})`);
  }
  return res.json();
}

/**
 * Retry a failed job.
 * @param {string} jobId
 * @returns {Promise<object>} Updated job object
 */
export async function lensRetryJob(jobId) {
  const res = await fetch(`${API}/api/lens/jobs/${jobId}/retry`, { method: 'POST' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Retry failed (${res.status})`);
  }
  return res.json();
}

/**
 * Upload + poll until terminal state. Returns final job object.
 * Calls onProgress(job) on each poll iteration.
 * @param {File} file
 * @param {object} [opts] - {patientId, documentTypeHint, onProgress, pollInterval, maxPolls}
 * @returns {Promise<object>} Final job (completed or failed)
 */
export async function lensExtractAndWait(file, opts = {}) {
  const { onProgress, pollInterval = 2000, maxPolls = 30, ...extractOpts } = opts;
  const job = await lensExtract(file, extractOpts);

  if (job.status === 'completed' || job.status === 'failed') {
    onProgress?.(job);
    return job;
  }

  // Poll until terminal
  let current = job;
  for (let i = 0; i < maxPolls; i++) {
    await new Promise(r => setTimeout(r, pollInterval));
    current = await lensPollJob(current.job_id);
    onProgress?.(current);
    if (current.status === 'completed' || current.status === 'failed') {
      return current;
    }
  }

  // Timeout
  current.status = 'failed';
  current.errors = [...(current.errors || []), 'Polling timeout — extraction took too long'];
  return current;
}
